import { useEffect, useRef, useState } from "react";
import { submitLead, resolveCRMProjectId } from "../services/crmService";
import { getContent } from "../state/siteContent";

/**
 * Floating action stack (bottom-right):
 *   • a scripted chat assistant ("Niti") that greets the visitor, answers a
 *     few common questions, and collects name + phone → pushes to the same
 *     CRM as the lead form.
 *   • a circular WhatsApp button below it.
 *
 * There's no live backend, so the assistant is a guided flow rather than a
 * free-form AI — it reliably captures a lead and hands off to WhatsApp/phone.
 */

const WA_NUMBER = () => {
  try { return getContent().settings.whatsappRaw || "919921210076"; }
  catch { return "919921210076"; }
};

// The scripted conversation. Each bot turn either shows quick-reply chips or
// asks for typed input; the flow ends by submitting a lead.
const QUICK = {
  start: [
    { label: "Book a site visit", next: "collectName", intent: "Site visit" },
    { label: "Get project details", next: "collectName", intent: "Project details" },
    { label: "Talk about pricing", next: "collectName", intent: "Pricing" },
    { label: "I'm an NRI buyer", next: "collectName", intent: "NRI enquiry" },
  ],
};

export default function WhatsAppButton() {
  const [visible, setVisible] = useState(true);
  const lastY = useRef(0);

  const [chatOpen, setChatOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [step, setStep] = useState("start");     // start | collectName | collectPhone | done
  const [input, setInput] = useState("");
  const [lead, setLead] = useState({ intent: "", name: "", phone: "" });
  const [sending, setSending] = useState(false);
  const bodyRef = useRef(null);

  // Show/hide the stack on scroll direction (ref, not state → no listener churn)
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setVisible(y < lastY.current || y < 80);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Seed the greeting the first time the chat opens
  useEffect(() => {
    if (chatOpen && msgs.length === 0) {
      setMsgs([
        { from: "bot", text: "Hi! I'm Niti from Nausiddhi Realty. 🌿" },
        { from: "bot", text: "How can I help you today?" },
      ]);
    }
  }, [chatOpen, msgs.length]);

  // Auto-scroll chat to newest message
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [msgs, sending]);

  const push = (m) => setMsgs((prev) => [...prev, m]);

  const handleQuick = (opt) => {
    push({ from: "user", text: opt.label });
    setLead((l) => ({ ...l, intent: opt.intent }));
    setStep("collectName");
    setTimeout(() => push({ from: "bot", text: "Great - may I have your name?" }), 400);
  };

  const submitTyped = async () => {
    const val = input.trim();
    if (!val) return;
    push({ from: "user", text: val });
    setInput("");

    if (step === "collectName") {
      setLead((l) => ({ ...l, name: val }));
      setStep("collectPhone");
      setTimeout(
        () => push({ from: "bot", text: `Thanks ${val.split(" ")[0]}! What's the best phone number to reach you?` }),
        400
      );
      return;
    }

    if (step === "collectPhone") {
      const digits = val.replace(/\D/g, "");
      if (digits.length < 10) {
        setTimeout(() => push({ from: "bot", text: "That doesn't look complete - please enter a 10-digit mobile number." }), 300);
        return;
      }
      const finalLead = { ...lead, phone: digits };
      setLead(finalLead);
      setStep("done");
      setSending(true);
      try {
        await submitLead({
          name: finalLead.name,
          phone: digits,
          projectId: resolveCRMProjectId("any"),
          remark: `Chatbot enquiry — ${finalLead.intent}`,
          leadType: "Website Chatbot",
        });
      } catch (_) {
        /* even if CRM fails, we still hand off to WhatsApp below */
      }
      setSending(false);
      setTimeout(() => {
        push({ from: "bot", text: "Perfect — our team will call you shortly. 📞" });
        push({ from: "bot", text: "Prefer to chat now? Tap below to continue on WhatsApp." });
      }, 500);
    }
  };

  const waHref = `https://wa.me/${WA_NUMBER()}${
    lead.name ? `?text=${encodeURIComponent(`Hi, I'm ${lead.name}. I'm interested in ${lead.intent || "your projects"}.`)}` : ""
  }`;

  return (
    <>
      <style>{`
        .fab-stack {
          position: fixed;
          bottom: 26px;
          right: 26px;
          z-index: 999;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 14px;
          transition: opacity 400ms ease, transform 400ms ease;
        }
        .fab-stack.is-hidden { opacity: 0; transform: translateY(16px); pointer-events: none; }

        /* Circular floating buttons */
        .fab {
          position: relative;
          width: 58px;
          height: 58px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          cursor: pointer;
          /* Icons inside use stroke="currentColor", so the FAB must declare
             its own foreground — otherwise they'd inherit dark body text and
             disappear against the coloured circle. */
          color: var(--cream-on-dark, #fbf7ee);
          box-shadow: 0 10px 30px rgba(0,0,0,0.28);
          transition: transform 300ms cubic-bezier(0.16,1,0.3,1), box-shadow 300ms ease;
        }
        .fab:hover { transform: translateY(-3px) scale(1.04); box-shadow: 0 16px 40px rgba(0,0,0,0.34); }

        .fab--wa  { background: #25D366; }
        .fab--bot { background: var(--color-green, #0b6b4f); }

        .fab__icon { width: 28px; height: 28px; display: block; }

        /* pulse ring on the WhatsApp button */
        .fab--wa::after {
          content: "";
          position: absolute; inset: 0; border-radius: 50%;
          border: 2px solid rgba(37, 211, 102, 0.5);
          animation: fab-pulse 2.4s ease-out infinite;
        }
        @keyframes fab-pulse {
          0% { transform: scale(1); opacity: 0.7; }
          100% { transform: scale(1.5); opacity: 0; }
        }

        /* little "notification" dot on the bot until first opened */
        .fab--bot .fab__dot {
          position: absolute; top: 2px; right: 2px;
          width: 12px; height: 12px; border-radius: 50%;
          background: var(--gold-on-dark); border: 2px solid #0b6b4f;
        }

        /* ── Chat panel ── */
        .chatbot {
          position: fixed;
          bottom: 100px;
          right: 26px;
          z-index: 1000;
          width: min(360px, calc(100vw - 40px));
          height: min(520px, calc(100vh - 150px));
          display: flex;
          flex-direction: column;
          background: #fbfcfa;
          border: 1px solid rgba(18,32,26,0.12);
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 24px 70px rgba(6,24,15,0.28);
          transform-origin: bottom right;
          animation: chat-in 300ms cubic-bezier(0.16,1,0.3,1) both;
        }
        @keyframes chat-in {
          from { opacity: 0; transform: translateY(14px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        .chatbot__head {
          display: flex; align-items: center; gap: 12px;
          padding: 16px 18px;
          background: linear-gradient(135deg, #0d3a2b, #0a2c22);
          color: var(--cream-on-dark);
        }
        .chatbot__avatar {
          width: 40px; height: 40px; border-radius: 50%; flex: none;
          display: grid; place-items: center;
          background: rgba(226,205,156,0.2);
          border: 1px solid rgba(226,205,156,0.5);
          font-family: var(--font-serif, serif); font-size: 20px; color: var(--gold-on-dark);
        }
        .chatbot__title { font-family: var(--font-ui, sans-serif); font-size: 14px; font-weight: 600; }
        .chatbot__status { font-size: 11px; color: rgba(241,245,239,0.7); display: flex; align-items: center; gap: 6px; }
        .chatbot__status::before { content:""; width:7px; height:7px; border-radius:50%; background:#4ade80; }
        .chatbot__close {
          margin-left: auto; background: none; border: none; color: rgba(241,245,239,0.8);
          cursor: pointer; font-size: 22px; line-height: 1; padding: 4px;
        }
        .chatbot__close:hover { color: #fff; }

        .chatbot__body {
          flex: 1; overflow-y: auto; padding: 16px;
          display: flex; flex-direction: column; gap: 10px;
          background:
            radial-gradient(120% 60% at 100% 0%, rgba(11,107,79,0.05), transparent 60%),
            #fbfcfa;
        }
        .chat-msg {
          max-width: 82%;
          padding: 10px 14px;
          font-family: var(--font-body, sans-serif);
          font-size: 14px; line-height: 1.5;
          border-radius: 14px;
        }
        .chat-msg--bot  { align-self: flex-start; background: #fff; border: 1px solid rgba(18,32,26,0.1); color: #12201a; border-bottom-left-radius: 4px; }
        .chat-msg--user { align-self: flex-end; background: var(--color-green, #0b6b4f); color: var(--cream-on-dark); border-bottom-right-radius: 4px; }

        .chat-typing { align-self: flex-start; display: flex; gap: 4px; padding: 12px 14px; background: #fff; border: 1px solid rgba(18,32,26,0.1); border-radius: 14px; }
        .chat-typing span { width: 6px; height: 6px; border-radius: 50%; background: rgba(18,32,26,0.4); animation: chat-dot 1.2s infinite; }
        .chat-typing span:nth-child(2) { animation-delay: 0.2s; }
        .chat-typing span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes chat-dot { 0%,60%,100%{opacity:0.3;transform:translateY(0)} 30%{opacity:1;transform:translateY(-3px)} }

        .chat-quick { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 2px; }
        .chat-quick button {
          font-family: var(--font-ui, sans-serif); font-size: 12.5px; font-weight: 500;
          padding: 9px 14px; border-radius: 999px; cursor: pointer;
          background: rgba(11,107,79,0.08); color: var(--color-green, #0b6b4f);
          border: 1px solid var(--color-green-faint, rgba(11,107,79,0.2));
          transition: background 200ms ease, transform 200ms ease;
        }
        .chat-quick button:hover { background: rgba(11,107,79,0.16); transform: translateY(-1px); }

        .chatbot__foot { border-top: 1px solid rgba(18,32,26,0.1); padding: 10px; background: #fff; }
        .chatbot__input-row { display: flex; gap: 8px; }
        .chatbot__input {
          flex: 1; padding: 11px 14px; border-radius: 999px;
          border: 1px solid rgba(18,32,26,0.16); font-size: 14px;
          font-family: var(--font-body, sans-serif); color: #12201a; outline: none;
          background: #fbfcfa;
        }
        .chatbot__input:focus { border-color: var(--color-green, #0b6b4f); }
        .chatbot__send {
          width: 42px; height: 42px; flex: none; border-radius: 50%; border: none; cursor: pointer;
          background: var(--color-green, #0b6b4f); color: #fff; display: grid; place-items: center;
        }
        .chatbot__send:disabled { opacity: 0.5; cursor: default; }
        .chatbot__wa-cta {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          margin: 8px 0 2px; padding: 12px; border-radius: 12px;
          background: #25D366; color: #fff; text-decoration: none;
          font-family: var(--font-ui, sans-serif); font-size: 13px; font-weight: 600;
          letter-spacing: 0.04em;
        }
        .chatbot__wa-cta svg { width: 18px; height: 18px; }

        @media (max-width: 540px) {
          .fab-stack { bottom: 20px; right: 18px; }
          .chatbot { bottom: 92px; right: 18px; }
        }
      `}</style>

      {/* Chat panel */}
      {chatOpen && (
        <div className="chatbot" role="dialog" aria-label="Chat with Nausiddhi Realty">
          <div className="chatbot__head">
            <div className="chatbot__avatar" aria-hidden="true">N</div>
            <div>
              <div className="chatbot__title">Niti · Nausiddhi Realty</div>
              <div className="chatbot__status">Typically replies instantly</div>
            </div>
            <button className="chatbot__close" onClick={() => setChatOpen(false)} aria-label="Close chat">×</button>
          </div>

          <div className="chatbot__body" ref={bodyRef}>
            {msgs.map((m, i) => (
              <div key={i} className={`chat-msg chat-msg--${m.from}`}>{m.text}</div>
            ))}

            {step === "start" && (
              <div className="chat-quick">
                {QUICK.start.map((o) => (
                  <button key={o.label} onClick={() => handleQuick(o)}>{o.label}</button>
                ))}
              </div>
            )}

            {sending && (
              <div className="chat-typing" aria-label="Sending"><span /><span /><span /></div>
            )}

            {step === "done" && !sending && (
              <a className="chatbot__wa-cta" href={waHref} target="_blank" rel="noreferrer">
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                </svg>
                Continue on WhatsApp
              </a>
            )}
          </div>

          {step !== "start" && step !== "done" && (
            <div className="chatbot__foot">
              <form
                className="chatbot__input-row"
                onSubmit={(e) => { e.preventDefault(); submitTyped(); }}
              >
                <input
                  className="chatbot__input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={step === "collectPhone" ? "Your phone number" : "Type your reply…"}
                  inputMode={step === "collectPhone" ? "tel" : "text"}
                  autoFocus
                />
                <button className="chatbot__send" type="submit" disabled={!input.trim()} aria-label="Send">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" />
                  </svg>
                </button>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Floating button stack */}
      <div className={`fab-stack ${visible ? "" : "is-hidden"}`}>
        <button
          type="button"
          className="fab fab--bot"
          onClick={() => setChatOpen((v) => !v)}
          aria-label={chatOpen ? "Close chat" : "Open chat"}
        >
          {!chatOpen && msgs.length === 0 && <span className="fab__dot" aria-hidden="true" />}
          {chatOpen ? (
            <svg className="fab__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          ) : (
            <svg className="fab__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7a8.5 8.5 0 0 1-.9-3.8A8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" />
              <path d="M8.5 12h.01M12 12h.01M15.5 12h.01" />
            </svg>
          )}
        </button>

        <a
          href={`https://wa.me/${WA_NUMBER()}`}
          target="_blank"
          rel="noreferrer"
          className="fab fab--wa"
          aria-label="Chat with us on WhatsApp"
        >
          {/* WhatsApp glyph: the speech-bubble outline + the phone handset are
              drawn as two separate paths so the handset reads clearly (a single
              combined path filled solid looks like a plain blob at this size). */}
          <svg className="fab__icon" viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <path
              fill="#fff"
              d="M16.001 4C9.373 4 4 9.373 4 16c0 2.114.552 4.174 1.6 5.99L4 28l6.18-1.616A11.94 11.94 0 0 0 16 28c6.627 0 12-5.373 12-12S22.628 4 16.001 4Zm0 21.82a9.8 9.8 0 0 1-4.995-1.368l-.358-.213-3.667.96.978-3.573-.234-.369A9.78 9.78 0 0 1 6.18 16c0-5.42 4.4-9.82 9.821-9.82 5.42 0 9.82 4.4 9.82 9.82 0 5.42-4.4 9.82-9.82 9.82Z"
            />
            <path
              fill="#fff"
              d="M21.393 18.42c-.293-.147-1.735-.856-2.004-.954-.269-.098-.464-.147-.66.147-.195.293-.757.954-.928 1.15-.171.196-.342.22-.635.073-.293-.147-1.238-.456-2.358-1.455-.872-.777-1.46-1.737-1.631-2.03-.171-.294-.018-.452.128-.598.132-.131.293-.342.44-.513.146-.171.195-.293.293-.489.098-.196.049-.367-.024-.513-.073-.147-.66-1.59-.904-2.177-.238-.572-.48-.494-.66-.503l-.562-.01c-.196 0-.513.073-.782.367-.269.293-1.026 1.003-1.026 2.446 0 1.443 1.05 2.837 1.197 3.033.147.196 2.067 3.156 5.008 4.426.7.302 1.246.483 1.671.618.702.223 1.341.192 1.846.116.563-.084 1.735-.709 1.98-1.394.244-.685.244-1.272.171-1.394-.073-.122-.269-.196-.562-.343Z"
            />
          </svg>
        </a>
      </div>
    </>
  );
}
